import React, { useState, useRef, useEffect } from 'react'
import { Button, Icon, List, ListItemProps, Popup, SemanticICONS } from 'semantic-ui-react'
import './DropdownInsideScrollableParent.css'

interface Props {
  options: ListItemData[]
  placeholder: string
  content: string | JSX.Element
  // add name if content is JSX.Element
  name?: string
  marginTop?: string
  marginLeft?: string
  width?: string
  buttonWidth?: string
  buttonMarginLeft?: string
  placeholderMarginLeft?: string
  hasError?: boolean
  className?: string
  iconName?: SemanticICONS
  iconColor?: string
  iconFontSize?: string
  popupRadius?: string
  popupMargin?: string
  onClick?: (event: React.SyntheticEvent<HTMLElement, Event>, data: ListItemProps, key?: number | string) => void
}

export interface ListItemData {
  key: string | number
  text: string | number | JSX.Element
  value: string | number
  selected: boolean | undefined
}

const DropdownInsideScrollableParent = (props: Props): JSX.Element => {
  const { options, content, name, marginTop, placeholder, width, placeholderMarginLeft, popupRadius, popupMargin,
    buttonWidth, marginLeft, className, hasError, iconColor, buttonMarginLeft, iconName, iconFontSize, onClick } = props

  const [close, setClose] = useState<boolean>(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null)

  /**
   * Measures container width when the width prop uses percentages.
   * Percentage-based widths don't resolve correctly in portal-rendered popups
   * because the popup is mounted in document.body, outside the table DOM tree.
   * ResizeObserver keeps the measurement in sync on window resize.
   */
  useEffect(() => {
    if (!containerRef.current || !width || !width.includes('%')) return
    const observer = new ResizeObserver(entries => {
      setMeasuredWidth(entries[0]!.contentRect.width)
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [width])

  const handleOnClick = (e: React.SyntheticEvent<HTMLElement, Event>, data: ListItemProps, selected: boolean, key: number | string) => {
    if (selected) {
      e.stopPropagation()
    } else {
      if (onClick) {
        setClose(true)
        onClick(e, data, key)
      }
    }
  }

  const renderPopupContent = () => {
    return (
      <List>
        {options.map(o => {
          return (
            <List.Item
              className='list-item-disp'
              style={{ padding: '.5rem .3rem', backgroundColor: o.selected ? '#CDF7EC' : '', borderRadius: '4px', cursor: o.selected ? 'no-drop' : 'pointer', fontWeight: 'bold' }}
              key={o.key}
              value={o.value.toString()}
              content={o.text}
              onClick={(e, data) => handleOnClick(e, data, o.selected ? o.selected : false, o.key)}
            />
          )
        })}
      </List>
    )
  }

  const renderContentButton = () => {
    const _content = content && typeof content !== 'object' && name !== '' ? true : false
    if (typeof content === 'object' && name !== '') {
      return <div style={{ marginLeft: placeholderMarginLeft ? placeholderMarginLeft : '-1rem', overflow: 'hidden', minWidth: 0 }}>{content}</div>
    }
    return (
      <p style={{ marginLeft: placeholderMarginLeft ? placeholderMarginLeft : '-1rem', float: 'left', maxWidth: '6rem', whiteSpace: 'nowrap', fontWeight: _content ? 'bold' : 'normal', opacity: _content ? 1 : .4, color: _content ? '#172533' : '' }}>
        {_content ? content : placeholder}
      </p>
    )
  }

  const renderPopupTrigger = () => {
    return (
      <Button
        className={className}
        content={renderContentButton()}
        icon={(<Icon name={iconName ? iconName : 'chevron down'} size='small' style={{ backgroundColor: 'white', color: iconColor, fontSize: iconFontSize ? iconFontSize : '' }} />)}
        labelPosition='right'
        style={{
          height: '2.7rem',
          minWidth: buttonWidth ? buttonWidth : '100%',
          maxWidth: buttonWidth ? buttonWidth : '100%',
          border: close && hasError ? '1px solid #FA5953' : close && !hasError ? '1px solid #E5EBF0' : '1px solid #6CE8C6',
          borderRadius: '4px',
          backgroundColor: 'white',
          marginTop: marginTop,
          boxShadow: 'none',
          display: 'flex',
          alignItems: 'center',
          marginLeft: buttonMarginLeft
        }}
      />
    )
  }

  const renderDropdownPopup = () => {
    /** Convert percentage-based width to measured pixels for the portal-rendered popup */
    const popupWidth = (width && width.includes('%') && measuredWidth)
      ? `${measuredWidth}px`
      : width

    return (
      <Popup
        id='popupId'
        style={{
          border: close && hasError ? '1px solid #FA5953' : close && !hasError ? '1px solid #BEC8C3' : '1px solid #6CE8C6',
          borderRadius: popupRadius ?? '4px',
          margin: popupMargin ?? 0,
          padding: '.8rem .5rem',
          maxWidth: popupWidth,
          minWidth: popupWidth,
          marginLeft: marginLeft
        }}
        basic
        trigger={renderPopupTrigger()}
        content={renderPopupContent()}
        on='click'
        position='bottom left'
        open={close ? false : undefined}
        onOpen={() => setClose(false)}
        onClose={() => setClose(true)}
        hideOnScroll
      />
    )
  }

  return (
    <div ref={containerRef} className='dropdown-scrollable-parent'>
      {renderDropdownPopup()}
    </div>
  )
}

export default DropdownInsideScrollableParent
